import React, { useState } from 'react';
import CouponForm from './CouponForm';
import CloseConfirmation from './CloseConfirmation';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../auth/AuthContext';
import PaymentOptions from './PaymentOptions';
import { useOffers } from './hooks/useOffers';
import CartDrawer from './CartDrawer';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../../context/PopupContext';

const OfferPopup = ({
  subtotal,
  cartItems,
  comboItems = [],
  onClose,
  selectedAddress,
  closeCartDrawer,
}) => {
  const navigate = useNavigate();
  const handleNavigation = () => {
    navigate('/profile', { state: { activeTab: 'orders' } });
  };
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const { showPopup } = usePopup();

  const {
    finalTotal,
    appliedOffers,
    discountAmount,
    validateCoupon,
    applyOnlineDiscount,
  } = useOffers({
    subtotal,
    cartItems,
    comboItems,
    couponCode,
  });

  const handlePaymentSelection = async (method, finalTotal) => {
    if (!user || !selectedAddress) return;
    setPaymentProcessing(true);

    if (method === 'Cash on Delivery') {
      const orderPayload = {
        order_id: `GLM-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        company_name: 'Glumist Pvt Ltd',
        no_of_invoices: 1,
        no_of_boxes: 1,
        customer_email: user.email,
        consignee_name: selectedAddress.name,
        consignee_phone: selectedAddress.phone,
        consignee_address: `${selectedAddress.addressLine1}, ${selectedAddress.addressLine2}`,
        consignee_pincode: selectedAddress.pincode,
        consignee_city: selectedAddress.city,
        consignee_state: selectedAddress.state,
        payment_method: 'cod',
        collectable_amount: finalTotal.toFixed(2),
        invoice_value: finalTotal.toFixed(2),
        box_details: [{ box_length: 15, box_breadth: 10, box_height: 10, box_weight: 1 }],
        invoice_details: [
          {
            invoice_number: `INV-${Date.now()}`,
            invoice_date: new Date().toISOString().split('T')[0],
            invoice_value: finalTotal.toFixed(2),
            hsn_code: '3304',
            tax_rate: 0,
            tax_value: 0,
          },
        ],
        coupon_code: couponCode,
        delivery_fee: finalTotal < 500 && method === 'Cash on Delivery' ? 70 : 0,
        sub_total: subtotal,
        discount_amount: discountAmount,
        final_total: finalTotal,
        products: cartItems.map((item) => ({
          id: item.id,
          productName: item.name,
          invoiceValue: item.price,
          quantity: item.quantity,
          variant: item.variant || '',
          sku: item.sku || '',
          packed: false,
        })),
        combos: comboItems.map((item) => ({
          id: item.id,
          comboName: item.name,
          invoiceValue: item.price,
          quantity: item.quantity,
          packed: false,
        })),
      };

      await fetch(`${process.env.REACT_APP_API_URL}/api/nimbus/create-shipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_SECRET_KEY,
        },
        body: JSON.stringify(orderPayload),
      });
      // ✅ GA4 Purchase Event for COD
if (window.gtag) {
  window.gtag('event', 'purchase', {
    transaction_id: orderPayload.order_id,
    value: finalTotal,
    currency: 'INR',
    payment_type: 'cod',
    coupon: couponCode,
    items: cartItems.map(item => ({
      item_id: item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price
    }))
  });
}

      showPopup('✅ COD Order Placed Successfully!');
      clearCart();
      onClose();
      closeCartDrawer();
      handleNavigation();
    } else {
      const roundedTotal = Number(finalTotal.toFixed(2));

      let order;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_SECRET_KEY,
          },
          body: JSON.stringify({ amount: roundedTotal }),
        });
        if (!res.ok) throw new Error('Server Error');
        order = await res.json();
      } catch (error) {
        showPopup('❌ Something went wrong. Please try again.');
        setPaymentProcessing(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Glumist',
        description: 'Order Payment',
        order_id: order.id,
        handler: async function (response) {
          const verifyRes = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/verify-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': process.env.REACT_APP_API_SECRET_KEY,
            },
            body: JSON.stringify(response),
          });

          const verifyData = await verifyRes.json();

          if (verifyData.success) {
            const orderPayload = {
              order_id: `GLM-${Date.now()}`,
              company_name: 'Glumist Pvt Ltd',
              no_of_invoices: 1,
              no_of_boxes: 1,
              customer_email: user.email,
              consignee_name: selectedAddress.name,
              consignee_phone: selectedAddress.phone,
              consignee_address: `${selectedAddress.addressLine1}, ${selectedAddress.addressLine2}`,
              consignee_pincode: selectedAddress.pincode,
              consignee_city: selectedAddress.city,
              consignee_state: selectedAddress.state,
              payment_method: 'prepaid',
              collectable_amount: 0,
              invoice_value: roundedTotal.toFixed(2),
              box_details: [{ box_length: 10, box_breadth: 10, box_height: 10, box_weight: 1 }],
              invoice_details: [
                {
                  invoice_number: `INV-${Date.now()}`,
                  invoice_date: new Date().toISOString().split('T')[0],
                  invoice_value: roundedTotal.toFixed(2),
                  hsn_code: '4901',
                  tax_rate: 0,
                  tax_value: 0,
                },
              ],
              coupon_code: couponCode,
              delivery_fee: 0,
              sub_total: subtotal,
              discount_amount: discountAmount,
              final_total: finalTotal,
              products: cartItems.map((item) => ({
                id: item.id,
                productName: item.name,
                invoiceValue: item.price,
                quantity: item.quantity,
                variant: item.variant || '',
                sku: item.sku || '',
                packed: false,
              })),
              combos: comboItems.map((item) => ({
                id: item.id,
                comboName: item.name,
                invoiceValue: item.price,
                quantity: item.quantity,
                packed: false,
              })),
            };

            await fetch(`${process.env.REACT_APP_API_URL}/api/nimbus/create-shipment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_API_SECRET_KEY,
              },
              body: JSON.stringify(orderPayload),
            });
            // ✅ GA4 Purchase Event for Online Payment
if (window.gtag) {
  window.gtag('event', 'purchase', {
    transaction_id: orderPayload.order_id,
    value: finalTotal,
    currency: 'INR',
    payment_type: 'prepaid',
    coupon: couponCode,
    items: cartItems.map(item => ({
      item_id: item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price
    }))
  });
}

            showPopup('✅ Payment Successful!');
            clearCart();
            onClose();
            closeCartDrawer();
            handleNavigation();
          } else {
            showPopup('❌ Payment Verification Failed.');
          }
        },
        prefill: {
          name: user.displayName || '',
          email: user.email || '',
        },
        theme: { color: '#7C3AED' },
      };

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      } else {
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    }

    setPaymentProcessing(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm sm:max-w-md md:max-w-lg max-h-[90vh] overflow-y-auto relative">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Complete Your Purchase</h2>
            <button
              onClick={() => setShowCloseConfirm(true)}
              className="text-white hover:text-gray-200 text-2xl transition-colors"
            >
              &times;
            </button>
          </div>
          <p className="text-xs sm:text-sm opacity-90 mt-1">Apply offers & choose payment</p>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          {appliedOffers.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <h3 className="font-medium text-green-800 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Offers Applied
              </h3>
              <ul className="space-y-1 text-sm text-green-700">
                {appliedOffers.map((offer, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="inline-block mr-1">•</span> {offer}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CouponForm couponCode={couponCode} setCouponCode={setCouponCode} validateCoupon={validateCoupon} />

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Total Amount:</span>
              <span className="text-xl sm:text-2xl font-bold text-purple-600">
                ₹{finalTotal.toFixed(2)}
              </span>
            </div>
            {discountAmount > 0 && (
              <div className="text-sm text-green-600 mt-1">
                You saved ₹{discountAmount.toFixed(2)}
              </div>
            )}
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">Select Payment Method</h3>
            <PaymentOptions
              selectedAddress={selectedAddress}
              onSelect={handlePaymentSelection}
              cartItems={cartItems}
              couponCode={couponCode}
              subtotal={subtotal}
            />
          </div>

          {paymentProcessing && (
            <div className="flex items-center justify-center py-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mr-2" />
              <span className="text-gray-600 text-sm">Processing your payment...</span>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Your payment is secure & encrypted. By continuing, you agree to our Terms of Service.
          </p>
        </div>
      </div>

      {showCloseConfirm && (
        <CloseConfirmation onConfirm={onClose} onCancel={() => setShowCloseConfirm(false)} />
      )}
    </div>
  );
};

export default OfferPopup;
